import { useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  BrainCircuit,
  CheckCircle2,
  FileText,
  Loader2,
  Send,
  Sparkles,
} from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  createCareerInterview,
  createCareerInterviewPack,
  createCareerResumeMatch,
  finishCareerInterview,
  generateCareerRoadmap,
  getMyGithubRepositories,
  getResumeReports,
  parseCareerJob,
  saveCareerRoadmap,
  sendCareerInterviewMessage,
} from '../api.js'
import { CAREER_ROLE_GROUPS, getCareerRoleMeta } from '../career-role-data.js'

const STORAGE_KEY = 'career_assistant_wizard_state'
const STEPS = ['选择岗位', '填写 JD', '解析要求', '匹配简历项目', '准备方案', '面试题', '模拟面试']

const safeList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (!value) return []
  return String(value).split(/[;；,，\n]/).map((item) => item.trim()).filter(Boolean)
}

const emptyState = {
  selectedRole: '',
  roleCategory: '',
  recommendedMajors: '',
  requiredSkills: '',
  jdText: '',
  sourceUrl: '',
  job: null,
  jdAnalysis: null,
  selectedResumeReportId: '',
  selectedGithubRepoId: '',
  manualResumeText: '',
  matchResult: null,
  interviewPack: null,
  interview: null,
  mockMessage: '',
  summary: null,
  currentStep: 0,
}

function loadInitialState(searchParams) {
  let stored = {}
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    stored = {}
  }
  const selectedRole = searchParams.get('role') || ''
  if (!selectedRole) return { ...emptyState, ...stored }
  const roleCategory = searchParams.get('category') || CAREER_ROLE_GROUPS[0]?.category || ''
  const meta = getCareerRoleMeta(selectedRole, roleCategory)
  return {
    ...emptyState,
    ...stored,
    selectedRole,
    roleCategory,
    recommendedMajors: safeList(meta.majors).join('、'),
    requiredSkills: safeList(meta.skills).join('、'),
    jdText: createTemplateJd(selectedRole, roleCategory, meta),
    currentStep: 1,
  }
}

function createTemplateJd(role, category, meta = {}) {
  const skills = safeList(meta.skills).join('、') || '沟通表达、项目经历、学习能力'
  const majors = safeList(meta.majors).join('、') || '相关专业'
  return [
    `岗位名称：${role || '目标岗位'}`,
    `岗位方向：${category || '学生求职'}`,
    `适合专业：${majors}`,
    `核心要求：${skills}`,
    '岗位职责：协助完成业务执行、资料整理、跨团队沟通和结果复盘。',
    '任职要求：具备相关专业基础，能清晰表达项目或实践经历，有责任心和学习能力。',
    '面试重点：岗位理解、过往经历、项目/作品表达、沟通协作和问题解决。',
  ].join('\n')
}

function ResultList({ title, items }) {
  const list = safeList(items)
  return (
    <div className="career-result-card">
      <h4>{title}</h4>
      {list.length ? (
        <ul>{list.map((item) => <li key={String(item)}>{String(item)}</li>)}</ul>
      ) : (
        <p className="career-muted">暂无内容</p>
      )}
    </div>
  )
}

function StepShell({ title, description, children }) {
  return (
    <section className="career-step-shell">
      <div>
        <span className="career-kicker">{title}</span>
        <p>{description}</p>
      </div>
      {children}
    </section>
  )
}

function JobAssistantPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState(() => loadInitialState(searchParams))
  const [resumeReports, setResumeReports] = useState([])
  const [githubProjects, setGithubProjects] = useState([])
  const [activeCategory, setActiveCategory] = useState(state.roleCategory || CAREER_ROLE_GROUPS[0]?.category || '')
  const [loading, setLoading] = useState('')
  const [error, setError] = useState('')
  const [careerRoadmap, setCareerRoadmap] = useState(null)
  const [careerRoadmapNotice, setCareerRoadmapNotice] = useState('')

  const currentStep = state.currentStep || 0
  const isBusy = Boolean(loading)

  const selectedResume = useMemo(
    () => resumeReports.find((item) => String(item.id) === String(state.selectedResumeReportId)),
    [resumeReports, state.selectedResumeReportId],
  )

  const selectedProject = useMemo(
    () => githubProjects.find((item) => String(item.id || item.fullName || item.full_name) === String(state.selectedGithubRepoId)),
    [githubProjects, state.selectedGithubRepoId],
  )

  const roleMeta = useMemo(
    () => getCareerRoleMeta(state.selectedRole, state.roleCategory || activeCategory),
    [activeCategory, state.roleCategory, state.selectedRole],
  )

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  useEffect(() => {
    let alive = true
    async function loadResources() {
      try {
        const [resumePayload, githubPayload] = await Promise.all([
          getResumeReports().catch(() => ({ items: [] })),
          getMyGithubRepositories().catch(() => ({ items: [] })),
        ])
        if (!alive) return
        setResumeReports(Array.isArray(resumePayload?.items) ? resumePayload.items : [])
        setGithubProjects(Array.isArray(githubPayload?.items) ? githubPayload.items : [])
      } catch {
        if (alive) {
          setResumeReports([])
          setGithubProjects([])
        }
      }
    }
    loadResources()
    return () => {
      alive = false
    }
  }, [])

  function patch(next) {
    setState((previous) => ({ ...previous, ...next }))
  }

  function selectRole(role, category) {
    const meta = getCareerRoleMeta(role, category)
    patch({
      selectedRole: role,
      roleCategory: category,
      recommendedMajors: safeList(meta.majors).join('、'),
      requiredSkills: safeList(meta.skills).join('、'),
      jdText: createTemplateJd(role, category, meta),
      job: null,
      jdAnalysis: null,
      matchResult: null,
      interviewPack: null,
      currentStep: 1,
    })
    setCareerRoadmap(null)
    setCareerRoadmapNotice('')
  }

  function buildResumeText() {
    const parts = []
    if (selectedResume) {
      parts.push(`简历记录：${selectedResume.fileName || selectedResume.id}`)
      parts.push(`总分：${selectedResume.resumeScore || ''}`)
      parts.push(`优势：${safeList(selectedResume.strengths).join('；')}`)
      parts.push(`问题：${safeList(selectedResume.issues).map((item) => item.title || item.description || item).join('；')}`)
      parts.push(`建议：${safeList(selectedResume.suggestions).map((item) => item.title || item.after || item).join('；')}`)
    }
    if (selectedProject) {
      parts.push(`GitHub 项目：${selectedProject.fullName || selectedProject.full_name || selectedProject.name}`)
      parts.push(`描述：${selectedProject.description || ''}`)
      parts.push(`语言：${selectedProject.primaryLanguage || selectedProject.primary_language || ''}`)
    }
    if (state.manualResumeText) parts.push(state.manualResumeText)
    return parts.filter(Boolean).join('\n')
  }

  function roadmapPayload() {
    return {
      targetPosition: state.selectedRole || state.job?.jobTitle || '',
      profile: {
        roleCategory: state.roleCategory,
        recommendedMajors: state.recommendedMajors,
        requiredSkills: state.requiredSkills,
      },
      existingProjects: selectedProject ? [selectedProject] : githubProjects.slice(0, 3),
      resumeData: buildResumeText(),
      requiredSkills: state.jdAnalysis?.skills || state.jdAnalysis?.requirements || roleMeta.skills,
    }
  }

  async function goNext() {
    setError('')
    if (currentStep === 1 && !state.job?.id) {
      setLoading('parse')
      try {
        const result = await parseCareerJob({ rawText: state.jdText, sourceUrl: state.sourceUrl })
        patch({ job: result.job, jdAnalysis: result.job?.parsed || null, currentStep: 2 })
      } catch (apiError) {
        setError(apiError.message || '岗位要求解析失败')
      } finally {
        setLoading('')
      }
      return
    }
    if (currentStep === 3 && !state.matchResult?.id) {
      if (!state.job?.id) {
        setError('请先解析岗位要求')
        return
      }
      const resumeText = buildResumeText()
      if (!resumeText.trim()) {
        setError('请先选择简历记录、GitHub 项目，或补充一段简历/项目内容')
        return
      }
      setLoading('match')
      try {
        const result = await createCareerResumeMatch(state.job.id, {
          resumeReportId: state.selectedResumeReportId || undefined,
          githubRepoId: state.selectedGithubRepoId || undefined,
          resumeText,
        })
        patch({ matchResult: result.match, currentStep: 4 })
      } catch (apiError) {
        setError(apiError.message || '匹配分析生成失败')
      } finally {
        setLoading('')
      }
      return
    }
    if (currentStep === 5 && !state.interviewPack) {
      if (!state.job?.id) {
        setError('请先解析岗位要求')
        return
      }
      setLoading('pack')
      try {
        const result = await createCareerInterviewPack(state.job.id, { resumeMatchId: state.matchResult?.id })
        patch({ interviewPack: result.pack, currentStep: 6 })
      } catch (apiError) {
        setError(apiError.message || '面试题生成失败')
      } finally {
        setLoading('')
      }
      return
    }
    patch({ currentStep: Math.min(currentStep + 1, STEPS.length - 1) })
  }

  async function handleGenerateCareerRoadmap() {
    setError('')
    setCareerRoadmapNotice('')
    setLoading('roadmap')
    try {
      const result = await generateCareerRoadmap(roadmapPayload())
      setCareerRoadmap(result)
    } catch (apiError) {
      setError(apiError.message || '岗位成长路线生成失败')
    } finally {
      setLoading('')
    }
  }

  async function handleSaveCareerRoadmap() {
    if (!careerRoadmap) return
    setError('')
    setCareerRoadmapNotice('')
    setLoading('roadmap-save')
    try {
      await saveCareerRoadmap({ ...roadmapPayload(), roadmap: careerRoadmap })
      setCareerRoadmapNotice('岗位成长路线已保存，今日任务已同步到学生成长中心。')
      window.setTimeout(() => navigate('/growth'), 500)
    } catch (apiError) {
      setError(apiError.message || '岗位成长路线保存失败')
    } finally {
      setLoading('')
    }
  }

  async function startInterview() {
    if (!state.job?.id) return
    setLoading('interview')
    setError('')
    try {
      const result = await createCareerInterview({
        jobId: state.job.id,
        resumeMatchId: state.matchResult?.id,
      })
      patch({ interview: result.session, currentStep: 6 })
    } catch (apiError) {
      setError(apiError.code === 'VIP_REQUIRED' ? '模拟面试、完整总结和复盘报告需要 VIP。' : apiError.message)
    } finally {
      setLoading('')
    }
  }

  async function sendMockMessage() {
    if (!state.interview?.id || !state.mockMessage.trim()) return
    setLoading('message')
    setError('')
    try {
      const result = await sendCareerInterviewMessage(state.interview.id, state.mockMessage)
      patch({ interview: result.session, mockMessage: '' })
    } catch (apiError) {
      setError(apiError.message || '发送失败')
    } finally {
      setLoading('')
    }
  }

  async function finishMockInterview() {
    if (!state.interview?.id) return
    setLoading('finish')
    setError('')
    try {
      const result = await finishCareerInterview(state.interview.id)
      patch({ interview: result.session, summary: result.session?.summary || null })
    } catch (apiError) {
      setError(apiError.message || '复盘生成失败')
    } finally {
      setLoading('')
    }
  }

  function CareerRoadmapPanel() {
    const roadmap = careerRoadmap
    return (
      <section className="career-roadmap-panel">
        <div className="career-roadmap-head">
          <div>
            <span>我的岗位成长路线</span>
            <h3>{roadmap?.position || state.selectedRole || state.job?.jobTitle || '当前岗位目标'}</h3>
            <p>把岗位要求、已有经历和能力差距转成学生成长中心可执行任务。</p>
          </div>
          <div className="career-action-row">
            <button className="secondary-button" type="button" onClick={handleGenerateCareerRoadmap} disabled={isBusy}>
              {loading === 'roadmap' ? <Loader2 className="spin-icon" size={16} /> : <Sparkles size={16} />}
              生成成长路线
            </button>
            {roadmap ? (
              <button className="primary-button" type="button" onClick={handleSaveCareerRoadmap} disabled={isBusy}>
                {loading === 'roadmap-save' ? <Loader2 className="spin-icon" size={16} /> : <CheckCircle2 size={16} />}
                开始执行路线
              </button>
            ) : null}
            {careerRoadmapNotice ? <Link className="primary-button" to="/growth">进入学生成长中心</Link> : null}
          </div>
        </div>
        {careerRoadmapNotice ? <p className="career-success-note">{careerRoadmapNotice}</p> : null}
        {roadmap ? (
          <>
            <div className="career-roadmap-grid">
              <ResultList title="岗位要求能力" items={roadmap.requiredSkills} />
              <ResultList title="用户已有能力" items={roadmap.userSkills} />
              <ResultList title="能力差距" items={roadmap.gaps} />
            </div>
            <div className="career-roadmap-stages">
              {safeList(roadmap.stages).map((stage, index) => (
                <article key={`${stage.title}-${index}`}>
                  <span>阶段 {index + 1}</span>
                  <strong>{stage.title}</strong>
                  <p>{stage.goal}</p>
                  <ul>{safeList(stage.tasks).map((task) => <li key={task}>{task}</li>)}</ul>
                </article>
              ))}
            </div>
          </>
        ) : (
          <p className="career-empty">生成后会展示当前岗位目标、岗位要求能力、已有能力、能力差距和下一步学习计划。</p>
        )}
      </section>
    )
  }

  function renderStep() {
    if (currentStep === 0) {
      return (
        <StepShell title="Step 1：选择岗位方向" description="先确定目标岗位，后续 JD、简历匹配、成长路线和面试题都会围绕它生成。">
          <div className="career-wizard-tabs">
            {CAREER_ROLE_GROUPS.map((group) => (
              <button className={activeCategory === group.category ? 'is-active' : ''} key={group.category} type="button" onClick={() => setActiveCategory(group.category)}>
                {group.category}
              </button>
            ))}
          </div>
          <div className="career-role-select-grid">
            {(CAREER_ROLE_GROUPS.find((group) => group.category === activeCategory)?.roles || []).map((role) => {
              const meta = getCareerRoleMeta(role, activeCategory)
              const selected = state.selectedRole === role
              return (
                <button className={selected ? 'is-selected' : ''} key={role} type="button" onClick={() => selectRole(role, activeCategory)}>
                  <strong>{role}</strong>
                  <span>{safeList(meta.majors).slice(0, 3).join('、')}</span>
                  <small>{safeList(meta.skills).slice(0, 4).join(' / ')}</small>
                </button>
              )
            })}
          </div>
        </StepShell>
      )
    }

    if (currentStep === 1) {
      return (
        <StepShell title="Step 2：填写岗位 JD" description="粘贴招聘描述；没有 JD 时，可以先使用岗位模板继续。">
          <label className="career-field">
            岗位链接
            <input value={state.sourceUrl} onChange={(event) => patch({ sourceUrl: event.target.value })} placeholder="Boss / 智联 / 拉勾链接可先放这里" />
          </label>
          <label className="career-field">
            岗位描述
            <textarea value={state.jdText} onChange={(event) => patch({ jdText: event.target.value })} rows={12} />
          </label>
        </StepShell>
      )
    }

    if (currentStep === 2) {
      const analysis = state.jdAnalysis || {}
      return (
        <StepShell title="Step 3：岗位要求解析" description="确认岗位核心能力、关键词、项目要求和面试重点。">
          {loading === 'parse' ? <p className="career-empty"><Loader2 className="spin-icon" size={16} /> 正在解析岗位要求...</p> : null}
          <div className="career-output-grid">
            <ResultList title="岗位要求能力" items={analysis.skills || analysis.requirements || roleMeta.skills} />
            <ResultList title="项目经历要求" items={analysis.projectKeywords || analysis.projects} />
            <ResultList title="面试重点" items={analysis.interviewFocus || analysis.questions} />
          </div>
        </StepShell>
      )
    }

    if (currentStep === 3) {
      return (
        <StepShell title="Step 4：匹配简历 / GitHub 项目" description="选择已有记录，或先补充一段项目和实践内容。">
          <div className="career-form-grid">
            <label className="career-field">
              选择简历分析记录
              <select value={state.selectedResumeReportId} onChange={(event) => patch({ selectedResumeReportId: event.target.value, matchResult: null })}>
                <option value="">暂不选择</option>
                {resumeReports.map((item) => <option key={item.id} value={item.id}>{item.fileName || `简历记录 ${item.id}`}</option>)}
              </select>
            </label>
            <label className="career-field">
              选择 GitHub 项目
              <select value={state.selectedGithubRepoId} onChange={(event) => patch({ selectedGithubRepoId: event.target.value, matchResult: null })}>
                <option value="">暂不选择</option>
                {githubProjects.map((item) => {
                  const id = item.id || item.fullName || item.full_name || item.name
                  return <option key={id} value={id}>{item.fullName || item.full_name || item.name}</option>
                })}
              </select>
            </label>
          </div>
          <label className="career-field">
            补充简历 / 项目内容
            <textarea value={state.manualResumeText} onChange={(event) => patch({ manualResumeText: event.target.value, matchResult: null })} rows={8} placeholder="没有历史记录时，可粘贴简历正文、项目经历、作品说明或实践经历。" />
          </label>
          <div className="career-action-row">
            <Link className="secondary-button" to="/more/resume"><FileText size={16} />上传简历</Link>
          </div>
        </StepShell>
      )
    }

    if (currentStep === 4) {
      const match = state.matchResult || {}
      return (
        <StepShell title="Step 5：生成求职准备方案" description="把匹配结果转成简历修改、项目包装、短期准备计划和岗位成长路线。">
          <div className="career-score-card">
            <strong>{match.matchScore ?? '--'}/100</strong>
            <span>岗位匹配度</span>
          </div>
          <div className="career-output-grid">
            <ResultList title="缺失能力" items={match.match?.gaps} />
            <ResultList title="简历修改建议" items={match.advice?.priorityActions || match.advice?.bulletRewrites} />
            <ResultList title="项目包装建议" items={match.advice?.projectPackaging} />
            <ResultList title="学习补强建议" items={match.match?.keywordsToAdd} />
            <ResultList title="7 天 / 14 天准备计划" items={match.prepPlan || match.advice?.priorityActions} />
          </div>
          <CareerRoadmapPanel />
        </StepShell>
      )
    }

    if (currentStep === 5) {
      const questions = state.interviewPack?.pack?.questions || state.matchResult?.questions || {}
      return (
        <StepShell title="Step 6：生成面试题" description="围绕自我介绍、项目追问、专业基础、岗位理解和行为面试练习。">
          {!state.interviewPack && <p className="career-empty">点击下一步时会生成完整面试题包。</p>}
          <div className="career-output-grid">
            <ResultList title="自我介绍" items={questions.selfIntro} />
            <ResultList title="项目追问" items={questions.project} />
            <ResultList title="行为面试" items={questions.behavior} />
            <ResultList title="准备计划" items={state.interviewPack?.pack?.prepPlan} />
          </div>
        </StepShell>
      )
    }

    return (
      <StepShell title="Step 7：模拟面试 / 复盘" description="文字模拟面试，逐题回答，最后生成复盘报告。">
        <div className="career-action-row">
          <button className="primary-button" type="button" onClick={startInterview} disabled={isBusy || !state.job?.id}>
            {loading === 'interview' ? <Loader2 className="spin-icon" size={17} /> : <Bot size={17} />}
            开始模拟面试
          </button>
        </div>
        {state.interview?.messages?.length ? (
          <div className="career-chat-box">
            {state.interview.messages.map((item, index) => (
              <article key={`${item.role}-${index}`}>
                <strong>{item.role === 'user' ? '我' : 'AI 面试官'}</strong>
                <p>{item.content}</p>
              </article>
            ))}
          </div>
        ) : <p className="career-empty">模拟面试需要 VIP；免费用户仍可查看题包并自行练习。</p>}
        {state.interview?.status === 'active' && (
          <div className="career-chat-input">
            <textarea value={state.mockMessage} onChange={(event) => patch({ mockMessage: event.target.value })} rows={4} placeholder="输入你的回答..." />
            <button className="secondary-button" type="button" onClick={finishMockInterview} disabled={isBusy}>
              {loading === 'finish' ? <Loader2 className="spin-icon" size={17} /> : <CheckCircle2 size={17} />}
              结束并复盘
            </button>
            <button className="primary-button" type="button" onClick={sendMockMessage} disabled={isBusy}>
              {loading === 'message' ? <Loader2 className="spin-icon" size={17} /> : <Send size={17} />}
              发送回答
            </button>
          </div>
        )}
        {state.summary ? <ResultList title="下一步计划" items={state.summary.nextPlan} /> : null}
      </StepShell>
    )
  }

  return (
    <main className="career-assistant-page">
      <section className="career-assistant-hero">
        <Link className="back-link" to="/growth"><ArrowLeft size={17} />返回学生成长中心</Link>
        <div>
          <span><BrainCircuit size={18} />岗位助手</span>
          <h1>从目标岗位到成长路线</h1>
          <p>一步一步完成岗位选择、JD 解析、简历项目匹配、准备计划、面试题和成长中心任务。</p>
        </div>
      </section>

      <section className="career-progress-strip">
        {STEPS.map((step, index) => (
          <button className={index === currentStep ? 'is-active' : index < currentStep ? 'is-done' : ''} key={step} type="button" onClick={() => patch({ currentStep: index })}>
            <span>{index + 1}</span>
            {step}
          </button>
        ))}
      </section>

      {error ? <p className="career-error">{error}</p> : null}
      {renderStep()}

      <div className="career-wizard-actions">
        <button className="secondary-button" type="button" onClick={() => patch({ currentStep: Math.max(currentStep - 1, 0) })} disabled={currentStep === 0 || isBusy}>
          <ArrowLeft size={17} />上一步
        </button>
        {currentStep < STEPS.length - 1 ? (
          <button className="primary-button" type="button" onClick={goNext} disabled={isBusy || (currentStep === 0 && !state.selectedRole)}>
            {isBusy ? <Loader2 className="spin-icon" size={17} /> : null}
            {currentStep === 1 ? '下一步：解析岗位要求' : currentStep === 3 ? '下一步：生成匹配分析' : currentStep === 5 ? '下一步：开始模拟面试' : '下一步'}
            <ArrowRight size={17} />
          </button>
        ) : null}
      </div>
    </main>
  )
}

export default JobAssistantPage
